import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Col,
  Form,
  Image,
  Row,
  Modal,
  Button,
  FloatingLabel,
} from "react-bootstrap";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import CustomButton from "../button/button.component";
import SimpleGoogleAutocomplete from "../place-autocomplete/place-autocomplete.component";
import { useMutation } from "@apollo/client";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ReactDOM from "react-dom/client";
import { MerchantAgreementTemplate } from "../../utils/merchant-agreement-template.component";
import LoadingSpinner from "../shared/loading-spinner/loading-spinner.component";
import {
  CREATE_EXTERNAL_BUSINESS_ACCOUNT,
  VERIFY_EXTERNAL_BUSINESS_ACCOUNT,
} from "../../services/graphql/externalBusinessAccounts";
import { toast } from "react-toastify";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../../firebase";
import { useDeviceDetection } from "../../Hooks/useDeviceDetection";

const BusinessRegisteration = ({ token, businessId }) => {
  const isMobile = useDeviceDetection(); // Enhanced mobile detection for larger phones
  const [targetBusinessId, setTargetBusinessId] = useState(null);
  const [showOTPVerificationComponent, setShowOTPVerificationComponent] =
    useState(false);
  const [validated, setValidated] = useState(false);
  const [errorFields, setErrorFields] = useState({});

  // Form state
  const [businessName, setBusinessName] = useState("");
  const [authorizedName, setAuthorizedName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [isPhoneValid, setPhoneValid] = useState(false);
  const [countryCode, setCountryCode] = useState(["us", "ca"]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [businessLocation, setBusinessLocation] = useState({
    address: "",
    cityName: "",
    stateName: "",
    countryName: "",
    streetName: "",
    lat: null,
    lng: null,
  });
  const [confirmationResult, setConfirmationResult] = useState("");
  const [otpCode, setOTPCode] = useState("");
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Terms and conditions state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);

  useEffect(() => {
    // Set the target business ID from the props (already decoded)
    if (businessId) {
      setTargetBusinessId(businessId);
      console.log("Target Business ID set from decoded QR code:", businessId);
    } else if (token) {
      console.log("Using token as fallback:", token);
      // Fallback: if no businessId prop, try to use token directly
      const mongoIdPattern = /^[0-9a-fA-F]{24}$/;
      if (mongoIdPattern.test(token)) {
        setTargetBusinessId(token);
        console.log("Target Business ID set from direct token:", token);
      }
    }
  }, [businessId, token]);

  useEffect(() => {
    const checkFormValidity = () => {
      const hasBusinessName =
        businessName.trim().length >= 2 && businessName.trim().length <= 100;
      const hasValidEmail = email.trim() && validateEmail(email.trim());
      const hasValidPhone = isPhoneValid;
      const hasLocation = businessLocation.address;

      setIsFormValid(
        hasBusinessName &&
          hasValidEmail &&
          hasValidPhone &&
          hasLocation &&
          termsAccepted
      );
    };

    checkFormValidity();
  }, [
    businessName,
    email,
    phoneNumber,
    businessLocation.address,
    isPhoneValid,
    termsAccepted,
  ]);

  // Uncheck terms and conditions when form data changes after they've been accepted
  useEffect(() => {
    if (termsAccepted) {
      setTermsAccepted(false);
      setGeneratedDocument(null); // Clear the generated document since data changed
      setHasScrolledToEnd(false);
    }
  }, [
    businessName,
    authorizedName,
    email,
    phoneNumber,
    businessLocation.address,
  ]);

  const [createExternalBusinessAccount, { loading: loadingCreatingAccount }] =
    useMutation(CREATE_EXTERNAL_BUSINESS_ACCOUNT, {
      onCompleted: (data) => {
        if (data?.createExternalBusinessAccount?.success) {
          toast.success(data.createExternalBusinessAccount.message, {
            style: { width: "450px" },
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored ",
          });

          // Redirect to website after successful registration
          setTimeout(() => {
            window.location.href = "https://www.myooo.co/aboutus";
          }, 3000); // Delay to allow toast to be visible
        } else {
          alert("Registration failed. Please try again.");
        }
      },
      onError: (error) => {
        console.error("Registration error:", error);
        alert("Registration failed. Please try again.");
      },
    });

  const [verifyExternalBusinessAccount, { loading: verifyingAccount }] =
    useMutation(VERIFY_EXTERNAL_BUSINESS_ACCOUNT, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data?.verifyExternalBusinessAccount?.success) {
          // Verification successful, show OTP component
          generateRecaptcha();
        } else {
          toast.error(data.verifyExternalBusinessAccount.message, {
            style: { width: "400px" },
          });
        }
      },
      onError: (error) => {
        // console.error("Verification error:", error);
        toast.error(error.message || "Verification failed. Please try again.", {
          style: { width: "400px" },
        });
      },
    });

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = {};

    // Business name validation
    if (!businessName.trim()) {
      errors.businessName = "Business name is required";
    } else if (businessName.trim().length < 2) {
      errors.businessName = "Business name must be at least 2 characters";
    } else if (businessName.trim().length > 100) {
      errors.businessName = "Business name must be less than 100 characters";
    }

    // Authorization name validation
    if (!authorizedName.trim()) {
      errors.authorizedName = "Authorized name is required";
    } else if (authorizedName.trim().length < 2) {
      errors.authorizedName = "Authorized name must be at least 2 characters";
    } else if (authorizedName.trim().length > 100) {
      errors.authorizedName =
        "Authorized name must be less than 100 characters";
    }

    // Phone validation
    if (!phoneNumber) {
      errors.phoneNumber = "Phone number is required";
    } else if (!isValidPhoneNumber(phoneNumber)) {
      errors.phoneNumber = "Please enter a valid phone number";
    }

    // Email validation
    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(email.trim())) {
      errors.email = "Please enter a valid email address";
    }

    // Location validation
    if (!businessLocation.address) {
      errors.location = "Business location is required";
    }

    setErrorFields(errors);
    return Object.keys(errors).length === 0;
  };

  // Function to download PDF
  const downloadPDF = (pdfBlob, fileName) => {
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateRecaptcha = () => {
    // Clear existing reCAPTCHA if it exists
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
        setRecaptchaVerifier(null);
      } catch (error) {
        console.log("Error clearing existing reCAPTCHA:", error);
      }
    }

    // Clear the container content to ensure clean state
    const container = document.getElementById("recaptcha-container");
    if (container) {
      container.innerHTML = "";
    }

    try {
      const appVerifier = new RecaptchaVerifier(
        "recaptcha-container",
        {
          size: "invisible",
          callback: (response) => {},
        },
        auth
      );

      setRecaptchaVerifier(appVerifier);
      onSignIn(appVerifier);
    } catch (error) {
      console.error("Error creating reCAPTCHA verifier:", error);
      toast.error(
        "Failed to initialize verification. Please refresh and try again.",
        {
          style: { width: "400px" },
        }
      );
    }
  };

  const onSignIn = (appVerifier) => {
    // console.log("onSignInmethod", appVerifier);
    signInWithPhoneNumber(auth, `+${phoneNumber}`, appVerifier)
      .then((confirmationResult) => {
        setConfirmationResult(confirmationResult);
        setShowOTPVerificationComponent(true);
        // console.log(confirmationResult);
      })
      .catch((error) => {
        // console.log(error);
        console.error("Error sending Firebase OTP:", error);
        toast.error("Failed to send verification code. Please try again.", {
          style: { width: "400px" },
        });
        setShowOTPVerificationComponent(false);
      });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const isFormValid = validateForm();
    // Terms validation
    if (!termsAccepted) {
      setErrorFields((prev) => ({
        ...prev,
        terms: "You must accept the terms and conditions",
      }));
      return;
    }
    setValidated(true);

    if (isFormValid) {
      try {
        // Call verification first to send OTP
        await verifyExternalBusinessAccount({
          variables: {
            verifyExternalBusinessAccountInputs: {
              businessName: businessName,
              businessPhone: phoneNumber,
            },
          },
        });
      } catch (error) {
        console.error("Error verifying business account:", error);
        toast.error(error.message || "Verification failed. Please try again.", {
          style: { width: "400px" },
        });
      }
    }
  };

  const handleLogin = async (user) => {
    try {
      // Generate unique IDs using high cardinality patterns for Azure Cosmos DB
      const timestamp = Date.now();
      const businessHash = businessName
        .trim()
        .replace(/\s+/g, "")
        .substring(0, 8)
        .toUpperCase();
      const fileName = `MA-${timestamp}-${businessHash}.pdf`;

      // Convert blob to File
      const pdfFile = new File([generatedDocument], fileName, {
        type: "application/pdf",
        lastModified: Date.now(),
      });

      const variables = {
        createExternalBusinessAccountInputs: {
          businessName: businessName.trim(),
          businessPhone: phoneNumber,
          businessEmail: email.trim().toLowerCase(),
          location: businessLocation.address,
          authorizedPersonName: authorizedName.trim(),
          signedPdf: pdfFile,
        },
      };
      console.log("Mutation Variables:", variables);

      // Execute the final account creation mutation
      await createExternalBusinessAccount({
        variables,
      });
      downloadPDF(pdfFile, fileName);
    } catch (error) {
      console.error("Error creating business account:", error);
      toast.error("Registration failed. Please try again.", {
        style: { width: "400px" },
      });
    }
  };

  const handleVerifyClick = () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP", {
        style: { width: "400px" },
      });
      return;
    }

    if (!confirmationResult) {
      toast.error("Verification session expired. Please request a new code.", {
        style: { width: "400px" },
      });
      setShowOTPVerificationComponent(false);
      return;
    }

    // Check if confirmationResult has the confirm method
    if (typeof confirmationResult.confirm !== "function") {
      console.error("Invalid confirmation result:", confirmationResult);
      toast.error("Invalid verification session. Please try again.", {
        style: { width: "400px" },
      });
      setShowOTPVerificationComponent(false);
      return;
    }

    confirmationResult
      .confirm(otpCode)
      .then((result) => {
        // User signed in successfully.
        const user = result.user;
        console.log("Firebase verification successful:", user.uid);
        handleLogin(user);
      })
      .catch((error) => {
        // User couldn't sign in (bad verification code?)
        console.error("OTP verification error:", error);
        toast.error("Invalid OTP Code, Please try again", {
          style: { width: "350px" },
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      });
  };

  const handleBackToForm = () => {
    console.log("handleBackToForm called"); // Debug log
    console.log(
      "Current showOTPVerificationComponent:",
      showOTPVerificationComponent
    ); // Debug log
    console.log("Current termsAccepted:", termsAccepted); // Debug log

    // Store current terms status
    const currentTermsStatus = termsAccepted;
    const currentGeneratedDoc = generatedDocument;
    const currentScrollStatus = hasScrolledToEnd;

    // Clean up reCAPTCHA verifier
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
        setRecaptchaVerifier(null);
      } catch (error) {
        console.log("Error clearing reCAPTCHA:", error);
      }
    }

    // Clear the reCAPTCHA container
    const container = document.getElementById("recaptcha-container");
    if (container) {
      container.innerHTML = "";
    }

    // Reset OTP related states
    setConfirmationResult("");
    setOTPCode("");
    setShowOTPVerificationComponent(false);

    console.log("Setting showOTPVerificationComponent to false"); // Debug log

    // Clear any validation errors that might have occurred during OTP process
    setValidated(false);

    // Only clear error fields, don't reset the entire object
    setErrorFields((prev) => ({
      ...prev,
      phoneNumber: "", // Clear phone errors
      // Keep other errors intact
    }));

    // Restore terms status after a brief delay to avoid useEffect interference
    setTimeout(() => {
      setTermsAccepted(currentTermsStatus);
      setGeneratedDocument(currentGeneratedDoc);
      setHasScrolledToEnd(currentScrollStatus);
    }, 10);

    // Form data (businessName, email, phoneNumber, etc.) is preserved automatically
    // as we're not resetting those state variables
  };

  const generateMerchantAgreement = async (businessData) => {
    setIsGeneratingPDF(true);
    try {
      // Create temporary container with enhanced styling for better PDF rendering
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.width = "210mm"; // A4 width
      container.style.backgroundColor = "white";
      container.style.fontFamily = "Times New Roman, serif";
      container.style.lineHeight = "1.6"; // Improved line spacing
      container.style.fontSize = "12px";
      container.style.padding = "0";
      container.style.margin = "0";
      // Add CSS to prevent line breaks in middle of sentences
      container.style.wordWrap = "break-word";
      container.style.hyphens = "auto";
      document.body.appendChild(container);

      // Render the merchant agreement template
      const root = ReactDOM.createRoot(container);
      await new Promise((resolve) => {
        root.render(
          <MerchantAgreementTemplate
            businessData={businessData}
            isPDFGeneration={true}
          />
        );
        setTimeout(resolve, 4000); // Increased wait time for complete rendering and layout
      });

      // Enhanced PDF generation with content-aware page breaks
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
        lineHeight: 1.6,
      });

      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 15; // Margin in mm
      const contentWidth = pageWidth - 2 * margin;
      const contentHeight = pageHeight - 2 * margin;

      // Generate high-quality canvas with better text rendering
      const canvas = await html2canvas(container, {
        scale: 3, // Higher DPI for better quality and text clarity
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        width: container.scrollWidth,
        height: container.scrollHeight,
        windowWidth: container.scrollWidth,
        windowHeight: container.scrollHeight,
        // Enhanced text rendering options
        letterRendering: true,
        logging: false,
        imageTimeout: 15000,
        removeContainer: true,
        foreignObjectRendering: false,
        // Ensure proper text rendering
        onclone: (clonedDoc) => {
          const clonedContainer = clonedDoc.querySelector("div");
          if (clonedContainer) {
            clonedContainer.style.webkitFontSmoothing = "antialiased";
            clonedContainer.style.mozOsxFontSmoothing = "grayscale";
          }
        },
      });

      // Calculate content dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Improved page break algorithm with content awareness
      const pageContentHeight = contentHeight;
      const totalPages = Math.ceil(imgHeight / pageContentHeight);

      // Calculate optimal break points to avoid splitting lines
      const lineHeight = 5; // Approximate line height in mm

      for (let currentPage = 0; currentPage < totalPages; currentPage++) {
        if (currentPage > 0) {
          pdf.addPage();
        }

        // Calculate the Y position for this page with content-aware breaks
        let yStart = currentPage * pageContentHeight;
        let yEnd = Math.min((currentPage + 1) * pageContentHeight, imgHeight);

        // Adjust break points to avoid splitting content mid-line
        if (currentPage < totalPages - 1) {
          // Not the last page
          // Look for a good break point (paragraph end or significant whitespace)
          const breakZone = lineHeight * 3; // 3 line buffer zone
          yEnd = yEnd - breakZone;

          // Find next good break point (this is simplified - in production you'd analyze the DOM)
          yEnd = Math.floor(yEnd / lineHeight) * lineHeight;
        }

        const sourceY = (yStart * canvas.height) / imgHeight;
        const sourceHeight = ((yEnd - yStart) * canvas.height) / imgHeight;

        // Create canvas section for this page
        const pageCanvas = document.createElement("canvas");
        const pageCtx = pageCanvas.getContext("2d");

        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;

        // Enable image smoothing for better quality
        pageCtx.imageSmoothingEnabled = true;
        pageCtx.imageSmoothingQuality = "high";

        // Draw the content portion onto the page canvas
        pageCtx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sourceHeight,
          0,
          0,
          canvas.width,
          sourceHeight
        );

        const pageImgData = pageCanvas.toDataURL("image/png", 1.0);
        const pageImgHeight = (sourceHeight * contentWidth) / canvas.width;

        // Add the image to PDF with proper margins and quality settings
        pdf.addImage(
          pageImgData,
          "PNG",
          margin,
          margin,
          imgWidth,
          pageImgHeight,
          undefined,
          "MEDIUM" // Better quality than 'FAST'
        );
      }

      // Cleanup
      root.unmount();
      document.body.removeChild(container);

      const pdfBlob = pdf.output("blob");
      setGeneratedDocument(pdfBlob);

      return pdfBlob;
    } catch (error) {
      console.error("Error generating merchant agreement PDF:", error);
      throw error;
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleAgreeTerms = async () => {
    const businessData = {
      businessName: businessName.trim(),
      authorizedName: authorizedName.trim(),
      email: email.trim(),
      phoneNumber,
      location: businessLocation,
      currentDate: getCurrentDate(),
    };

    try {
      // Generate the merchant agreement PDF
      await generateMerchantAgreement(businessData);

      setTermsAccepted(true);
      setShowTermsModal(false);

      if (errorFields.terms) {
        setErrorFields((prev) => ({ ...prev, terms: "" }));
      }
    } catch (error) {
      console.error("Error generating agreement:", error);
      alert("Error generating agreement. Please try again.");
    }
  };

  const handlePhoneChange = (value) => {
    setPhoneNumber(value || "");
    const isValid = value && isValidPhoneNumber(value);
    setPhoneValid(isValid);

    // Clear phone error when user starts typing a valid number
    if (isValid && errorFields.phoneNumber) {
      setErrorFields((prev) => ({ ...prev, phoneNumber: "" }));
    }
  };

  const handleLocationSelect = (data) => {
    // Handle both selection and clearing of location
    if (data && data.address) {
      // Location selected
      setBusinessLocation(data);
      // Clear location error when user selects a location
      if (errorFields.location) {
        setErrorFields((prev) => ({ ...prev, location: "" }));
      }
    } else {
      // Location cleared - reset to initial state
      setBusinessLocation({
        address: "",
        cityName: "",
        stateName: "",
        countryName: "",
        streetName: "",
        lat: null,
        lng: null,
      });
    }
  };

  const handleTermsCheckboxChange = (e) => {
    if (e.target.checked) {
      setShowTermsModal(true);
    } else {
      setTermsAccepted(false);
      setHasScrolledToEnd(false);
      // Clear terms error when unchecked
      if (errorFields.terms) {
        setErrorFields((prev) => ({ ...prev, terms: "" }));
      }
    }
  };

  const handleTermsModalScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
    if (isAtBottom) {
      setHasScrolledToEnd(true);
    }
  };

  const handleCloseTermsModal = () => {
    setShowTermsModal(false);
    setHasScrolledToEnd(false);
    // If they close without agreeing, uncheck the checkbox
    if (!termsAccepted) {
      // The checkbox will remain unchecked
    }
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Create business data for modal preview
  const getBusinessDataForPreview = () => ({
    businessName: businessName.trim() || "[Business Name]",
    authorizedName: authorizedName.trim() || "[Authorization Name]",
    email: email.trim() || "[Email Address]",
    phoneNumber: phoneNumber || "[Phone Number]",
    location: {
      address: businessLocation.address || "[Business Address]",
      cityName: businessLocation.cityName || "[City]",
      stateName: businessLocation.stateName || "[State]",
      countryName: businessLocation.countryName || "[Country]",
    },
    currentDate: getCurrentDate(),
  });

  return (
    <>
      <div
        style={{
          width: "97%",
          marginLeft: "3%",
          marginTop: "1%",
          ...(isMobile && {
            marginTop: "2%",
            width: "95%",
            marginLeft: "2.5%",
          }),
        }}
      >
        <Image
          src="../../images/FinalLogo.svg"
          alt="My..."
          style={{
            marginRight: 20,
            height: isMobile ? "8vh" : "6%",
            width: isMobile ? "auto" : "6%",
            maxWidth: "200px",
          }}
        />
      </div>
      <div
        style={
          showOTPVerificationComponent
            ? {
                width: "100%",
                height: isMobile ? "auto" : "80vh",
                // minHeight: isMobile ? "100vh" : "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: isMobile ? "flex-start" : "center",
                backgroundImage: 'url("../../images/LoginBackground.png")',
                backgroundSize: "cover",
                overflow: isMobile ? "auto" : "hidden",
                paddingTop: isMobile ? "20px" : "0",
                paddingBottom: isMobile ? "20px" : "0",
                marginBottom: "5%",
              }
            : {
                width: "100%",
                // height: isMobile ? "auto" : "100vh",
                // minHeight: isMobile ? "100vh" : "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: isMobile ? "flex-start" : "center",
                backgroundImage: 'url("../../images/LoginBackground.png")',
                backgroundSize: "cover",
                overflow: isMobile ? "auto" : "hidden",
                paddingTop: isMobile ? "20px" : "0",
                paddingBottom: isMobile ? "20px" : "0",
                marginBottom: "5%",
              }
        }
      >
        {!showOTPVerificationComponent ? (
          <>
            <Row style={{ justifyContent: "center" }}>
              <Col lg="10" md="10" sm="12" xs="12" className="Auth-form">
                <div
                  style={{
                    marginLeft: isMobile ? "16px" : "24px",
                    marginRight: isMobile ? "16px" : "24px",
                    marginTop: "3%",
                  }}
                >
                  <label
                    className="netural-black"
                    style={{
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: "bold",
                    }}
                  >
                    Register Your Business
                  </label>
                  <label
                    className="netural-grey"
                    style={{
                      fontSize: isMobile ? "14px" : "16px",
                    }}
                  >
                    Create your business account to get started.
                  </label>

                  <Form
                    noValidate
                    validated={validated}
                    onSubmit={handleSubmit}
                  >
                    {/* Business Name Field */}
                    <div style={{ marginTop: "5%" }}>
                      <Form.Group controlId="businessName">
                        <Form.Label>Business Name*</Form.Label>
                        <Form.Control
                          required
                          type="text"
                          placeholder="Enter your business name"
                          value={businessName}
                          onChange={(e) => {
                            setBusinessName(e.target.value);
                            // Clear error when user starts typing
                            if (errorFields.businessName) {
                              setErrorFields((prev) => ({
                                ...prev,
                                businessName: "",
                              }));
                            }
                          }}
                          isInvalid={!!errorFields.businessName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errorFields.businessName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    {/* Authorized Name Field */}
                    <div style={{ marginTop: "5%" }}>
                      <Form.Group controlId="authorizedName">
                        <Form.Label>Authorization Name*</Form.Label>
                        <Form.Control
                          required
                          type="text"
                          placeholder="Enter authorized person name"
                          value={authorizedName}
                          onChange={(e) => {
                            setAuthorizedName(e.target.value);
                            // Clear error when user starts typing
                            if (errorFields.authorizedName) {
                              setErrorFields((prev) => ({
                                ...prev,
                                authorizedName: "",
                              }));
                            }
                          }}
                          isInvalid={!!errorFields.authorizedName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errorFields.authorizedName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    {/* Email Field */}
                    <div style={{ marginTop: "5%" }}>
                      <Form.Group controlId="email">
                        <Form.Label>Email Address*</Form.Label>
                        <Form.Control
                          required
                          type="email"
                          placeholder="Enter your email address"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            // Clear error when user starts typing
                            if (errorFields.email) {
                              setErrorFields((prev) => ({
                                ...prev,
                                email: "",
                              }));
                            }
                          }}
                          isInvalid={!!errorFields.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errorFields.email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    {/* Phone Number Field */}
                    <div style={{ marginTop: "5%" }}>
                      <Form.Group controlId="phoneNumber">
                        <Form.Label>Phone Number*</Form.Label>
                        <PhoneInputWithCountrySelect
                          country={"US"}
                          value={phoneNumber}
                          enableSearch
                          onChange={handlePhoneChange}
                          international
                          defaultCountry="US"
                          style={{
                            border: errorFields.phoneNumber
                              ? "1px solid #dc3545"
                              : undefined,
                          }}
                        />
                        {errorFields.phoneNumber && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875em",
                              marginTop: "0.25rem",
                            }}
                          >
                            {errorFields.phoneNumber}
                          </div>
                        )}
                      </Form.Group>
                    </div>

                    {/* Business Location Field */}
                    <div style={{ marginTop: "5%" }}>
                      <Form.Group controlId="businessLocation">
                        <Form.Label>Business Location*</Form.Label>
                        <SimpleGoogleAutocomplete
                          onSelect={handleLocationSelect}
                          onClear={() => {
                            // Handle clearing when X icon is clicked
                            setBusinessLocation({
                              address: "",
                              cityName: "",
                              stateName: "",
                              countryName: "",
                              streetName: "",
                              lat: null,
                              lng: null,
                            });
                          }}
                          showClearIcon={true}
                          countryCodes={countryCode}
                          initialValue={businessLocation}
                          placeholder="Enter your business address"
                          showLabel={false}
                        />
                        {errorFields.location && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875em",
                              marginTop: "0.25rem",
                            }}
                          >
                            {errorFields.location}
                          </div>
                        )}
                      </Form.Group>
                    </div>

                    {/* Terms and Conditions Checkbox */}
                    <div style={{ marginTop: "7%" }}>
                      <Form.Group controlId="termsConditions">
                        <Form.Check
                          type="checkbox"
                          id="terms-checkbox"
                          label="I agree to the Terms and Conditions*"
                          checked={termsAccepted}
                          onChange={(e) => {
                            const isFormValid = validateForm();
                            console.log(
                              "isFormValid on terms change:",
                              isFormValid
                            );
                            if (isFormValid) {
                              handleTermsCheckboxChange(e);
                            } else {
                              toast.error(
                                "Please fill all required fields before accepting the terms.",
                                {
                                  style: { width: "400px" },
                                }
                              );
                            }
                          }}
                          isInvalid={!!errorFields.terms}
                          style={{ fontSize: "14px" }}
                        />
                        {errorFields.terms && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875em",
                              marginTop: "0.25rem",
                            }}
                          >
                            {errorFields.terms}
                          </div>
                        )}
                      </Form.Group>
                    </div>

                    <div
                      style={{
                        marginBottom: "40px",
                      }}
                    >
                      <CustomButton
                        title={"Send Verification Code"}
                        buttonType={"primary"}
                        type="submit"
                        disabled={!isFormValid || verifyingAccount}
                        loading={verifyingAccount}
                        style={{
                          opacity: isFormValid ? 1 : 0.6,
                          cursor: isFormValid ? "pointer" : "not-allowed",
                        }}
                      />
                    </div>
                  </Form>
                </div>
              </Col>
            </Row>
            <div id="recaptcha-container"></div>
            {/* Terms and Conditions Modal with MerchantAgreementTemplate */}
            <Modal
              show={showTermsModal}
              onHide={handleCloseTermsModal}
              size={isMobile ? "lg" : "lg"}
              fullscreen={isMobile ? "sm-down" : false}
              centered={!isMobile}
            >
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: isMobile ? "18px" : "20px" }}>
                  Merchant Agreement - Terms and Conditions
                </Modal.Title>
              </Modal.Header>
              <Modal.Body
                style={{
                  maxHeight: isMobile ? "70vh" : "60vh",
                  overflowY: "auto",
                  padding: isMobile ? "15px" : "20px",
                }}
                onScroll={handleTermsModalScroll}
              >
                {/* Use the MerchantAgreementTemplate component */}
                {isGeneratingPDF ? (
                  <LoadingSpinner text="Generating contract file." />
                ) : null}
                <div
                  style={{
                    width: "100%",
                  }}
                >
                  <MerchantAgreementTemplate
                    businessData={getBusinessDataForPreview()}
                  />
                  {hasScrolledToEnd && (
                    <div
                      style={{
                        backgroundColor: "#f8f9fa",
                        padding: "20px",
                        borderRadius: "8px",
                        border: "1px solid #dee2e6",
                        marginTop: "20px",
                      }}
                    >
                      <h5 style={{ marginBottom: "16px", color: "#495057" }}>
                        Agreement Summary
                      </h5>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#6c757d",
                          lineHeight: "1.5",
                        }}
                      >
                        <p>
                          <strong>Business Name:</strong>{" "}
                          {businessName || "Not provided"}
                        </p>
                        <p>
                          <strong>Authorization Name:</strong>{" "}
                          {authorizedName || "Not provided"}
                        </p>
                        <p>
                          <strong>Email:</strong> {email || "Not provided"}
                        </p>
                        <p>
                          <strong>Phone:</strong>{" "}
                          {phoneNumber || "Not provided"}
                        </p>
                        <p>
                          <strong>Address:</strong>{" "}
                          {businessLocation.address || "Not provided"}
                        </p>
                        <p>
                          <strong>Date:</strong> {getCurrentDate()}
                        </p>
                      </div>
                      <p
                        style={{
                          fontSize: "12px",
                          fontStyle: "italic",
                          color: "#868e96",
                          marginTop: "12px",
                        }}
                      >
                        By clicking "I Agree", you confirm that the above
                        information is correct and you accept all terms.
                      </p>
                    </div>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <CustomButton
                    title={
                      isGeneratingPDF
                        ? "Generating Agreement..."
                        : "I Agree & Generate Document"
                    }
                    buttonType={"primary"}
                    disabled={!hasScrolledToEnd || isGeneratingPDF}
                    customStyle={{
                      opacity: hasScrolledToEnd && !isGeneratingPDF ? 1 : 0.6,
                      cursor:
                        hasScrolledToEnd && !isGeneratingPDF
                          ? "pointer"
                          : "not-allowed",
                    }}
                    handleButtonClick={
                      !hasScrolledToEnd ? null : handleAgreeTerms
                    }
                  />
                </div>
              </Modal.Footer>
            </Modal>
          </>
        ) : (
          <>
            <Row style={{ justifyContent: "center" }}>
              <Col lg="10" md="10" sm="12" xs="12" className="Auth-form">
                <div
                  style={{
                    marginLeft: isMobile ? "16px" : "24px",
                    marginRight: isMobile ? "16px" : "24px",
                    marginTop: "3%",
                  }}
                >
                  <label
                    className="netural-black"
                    style={{
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: "bold",
                    }}
                  >
                    Verify Your Phone Number
                  </label>
                  <label
                    className="netural-grey"
                    style={{
                      fontSize: isMobile ? "14px" : "16px",
                    }}
                  >
                    Enter the 6-digit code sent to {phoneNumber}
                  </label>

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleVerifyClick();
                    }}
                  >
                    <div style={{ marginTop: "32px" }}>
                      <Form.Group controlId="email">
                        <Form.Label>Enter OTP</Form.Label>
                        <Form.Control
                          required
                          type="text"
                          placeholder="Enter OTP"
                          value={otpCode}
                          maxLength={6}
                          pattern="[0-9]{6}"
                          onChange={(event) => {
                            let otpCodeValue = event.target.value;
                            setOTPCode(otpCodeValue);
                          }}
                        />
                      </Form.Group>
                    </div>

                    <div
                      style={{
                        marginTop: "32px",
                      }}
                    >
                      <CustomButton
                        title={"Verify & Register"}
                        buttonType={"primary"}
                        type="submit"
                        disabled={
                          !otpCode ||
                          otpCode.length !== 6 ||
                          loadingCreatingAccount
                        }
                        loading={loadingCreatingAccount}
                        style={{
                          opacity:
                            otpCode &&
                            otpCode.length === 6 &&
                            !loadingCreatingAccount
                              ? 1
                              : 0.6,
                          cursor:
                            otpCode &&
                            otpCode.length === 6 &&
                            !loadingCreatingAccount
                              ? "pointer"
                              : "not-allowed",
                        }}
                      />
                    </div>
                  </Form>

                  <div style={{ textAlign: "center", marginTop: "5%" }}>
                    <CustomButton
                      title={"Back to Form"}
                      buttonType={"secondary"}
                      disabled={loadingCreatingAccount}
                      loading={loadingCreatingAccount}
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#6c757d",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: loadingCreatingAccount
                          ? "not-allowed"
                          : "pointer",
                        opacity: loadingCreatingAccount ? 0.6 : 1,
                      }}
                      handleButtonClick={handleBackToForm}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
};

export default BusinessRegisteration;
